import { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';

function DualRangeSlider({
    min = 0,
    max = 1000,
    defaultFromValue = 0,
    defaultToValue = 1000,
    onChange = () => {}
}) {
    const [fromValue, setFromValue] = useState(defaultFromValue);
    const [toValue, setToValue] = useState(defaultToValue);
    const [isDragging, setIsDragging] = useState(false);
    const [activeDragger, setActiveDragger] = useState(null);

    const rangeRef = useRef(null);
    const fromDraggerRef = useRef(null);
    const toDraggerRef = useRef(null);

    const getPercent = (value) => {
        return ((value - min) / (max - min)) * 100;
    };

    useEffect(() => {
        if (rangeRef.current) {
            const fromPercent = getPercent(fromValue);
            const toPercent = getPercent(toValue);
            rangeRef.current.style.left = `${fromPercent}%`;
            rangeRef.current.style.width = `${toPercent - fromPercent}%`;
        }
        onChange({ from: fromValue, to: toValue });
    }, [fromValue, toValue, min, max]);

    const handleFromInputChange = (e) => {
        const value = Math.min(Number(e.target.value), toValue);
        setFromValue(Math.max(min, value));
    };

    const handleToInputChange = (e) => {
        const value = Math.max(Number(e.target.value), fromValue);
        setToValue(Math.min(max, value));
    };

    const handleMouseDown = (e, dragger) => {
        setIsDragging(true);
        setActiveDragger(dragger);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const container = document.querySelector('.slider-container');
                const rect = container.getBoundingClientRect();
                const position = (e.clientX - rect.left) / rect.width;
                const newValue = Math.round(position * (max - min) + min);

                if (activeDragger === 'from') {
                    setFromValue(Math.max(min, Math.min(toValue, newValue)));
                } else {
                    setToValue(Math.min(max, Math.max(fromValue, newValue)));
                }
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, activeDragger, min, max, fromValue, toValue]);

    return (
        <>
            <div className="dual-range-slider">
                <div className="slider-container">
                    <div className="slider-track">
                        <div ref={rangeRef} className="slider-range" />
                    </div>

                    <div
                        ref={fromDraggerRef}
                        className="slider-dragger"
                        style={{ left: `${getPercent(fromValue)}%` }}
                        onMouseDown={(e) => handleMouseDown(e, 'from')}
                    >
                        <div className="slider-value">{fromValue}</div>
                    </div>

                    <div
                        ref={toDraggerRef}
                        className="slider-dragger"
                        style={{ left: `${getPercent(toValue)}%` }}
                        onMouseDown={(e) => handleMouseDown(e, 'to')}
                    >
                        <div className="slider-value">{toValue}</div>
                    </div>
                </div>
            </div>

            <style>{`
                .dual-range-slider {
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                }

                .slider-container {
                    position: relative;
                    height: 48px;
                    width: 100%;
                }

                .slider-track {
                    position: absolute;
                    top: 24px;
                    height: 4px;
                    width: 100%;
                    background-color: #d1d5db;
                    border-radius: 9999px;
                }

                .slider-range {
                    position: absolute;
                    height: 100%;
                    background-color: #ee373b;
                    border-radius: 9999px;
                }

                .slider-dragger {
                    position: absolute;
                    top: 20px;
                    width: 16px;
                    height: 16px;
                    background-color: #ee373b;
                    border: 2px solid #ee373b;
                    border-radius: 50%;
                    cursor: pointer;
                    transform: translateX(-50%);
                }

                .slider-value {
                    position: absolute;
                    top: -32px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 1rem;
                    font-weight: bold;
                    color: #4b5563;
                }
            `}</style>
        </>
    );
}

const WinHistory = () => {
    const [winHistory, setWinHistory] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [priceRange, setPriceRange] = useState([0, 1000000]);
    const carsPerPage = 5; // Set the number of rows per page to 5

    useEffect(() => {
        const fetchWinHistory = async () => {
            try {
                const dealerId = Cookies.get('adminSellerId'); // Assuming dealerId is stored in cookies
                const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/dealer/manage/getWinningHistory?dealerId=${dealerId}`, {
                    credentials: "include",
                    headers: {
                        "X-Full-URL": window.location.href, // Optional: full URL
                    }
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();

                // Process the data to split makeModelVariant into make, model, and variant
                const processedData = data.map(item => {
                    // Assuming makeModelVariant is in the format "Make Model Variant"
                    const parts = item.makeModelVariant.split(' ');
                    const make = parts[0];
                    const model = parts[1];
                    const variant = parts.slice(2).join(' ');

                    return {
                        ...item,
                        make,
                        model,
                        variant
                    };
                });

                console.log(processedData);
                setWinHistory(processedData);
            } catch (error) {
                console.error('Error fetching win history:', error);
            }
        };

        fetchWinHistory();
    }, []);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to the first page when searching
    };

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handlePriceRangeChange = (range) => {
        setPriceRange([range.from, range.to]);
        setCurrentPage(1); // Reset to the first page when changing the price range
    };

    const filteredWinHistory = winHistory
        .filter(win =>
            (win.make && win.make.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (win.year && win.year.toString().includes(searchTerm)) ||
            (win.winningPrice && win.winningPrice.toString().includes(searchTerm))
        )
        .filter(win => win.winningPrice >= priceRange[0] && win.winningPrice <= priceRange[1])
        .sort((a, b) => {
            if (!sortConfig.key) return 0;
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });

    // const paginatedWinHistory = filteredWinHistory.slice((currentPage - 1) * carsPerPage, currentPage * carsPerPage);

    // Function to format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(value);
    };

    // Function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Pagination logic
    const totalPages = Math.ceil(filteredWinHistory.length / carsPerPage);
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    return (
        <div className="win-history-container">
            <div className="page-header">
                <h1>Win History</h1>
            </div>

            <div className="filters-container">
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Search win history..."
                        value={searchTerm}
                        onChange={handleSearch}
                        id="search-input"
                    />
                </div>

                <div className="price-filter">
                    <label htmlFor="price-range-input">Price Range: {formatCurrency(priceRange[0])} - {formatCurrency(priceRange[1])}</label>
                    <DualRangeSlider
                        min={0}
                        max={1000000}
                        defaultFromValue={priceRange[0]}
                        defaultToValue={priceRange[1]}
                        onChange={handlePriceRangeChange}
                    />
                </div>
            </div>

            <div className="table-container">
                <table id="win-history-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort('registerNumber')}>
                                Register Number
                                {sortConfig.key === 'registerNumber' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                            <th onClick={() => handleSort('year')}>
                                Car Details
                                {sortConfig.key === 'year' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                            <th onClick={() => handleSort('basePrice')}>
                                Base Price
                                {sortConfig.key === 'basePrice' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                            <th onClick={() => handleSort('winningPrice')}>
                                Winning Price
                                {sortConfig.key === 'winningPrice' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                            <th onClick={() => handleSort('event')}>
                                Event
                                {sortConfig.key === 'event' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                            <th onClick={() => handleSort('eventDate')}>
                                Event Date
                                {sortConfig.key === 'eventDate' && (
                                    <span className="sort-indicator">{sortConfig.direction === 'ascending' ? ' ↑' : ' ↓'}</span>
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredWinHistory.length > 0 ? (
                            filteredWinHistory.map((win) => (
                                <tr key={win.registerNumber}>
                                    <td className="table-cell">{win.registrationNumber}</td>
                                    <td className="table-cell">{`${win.make} ${win.model} ${win.variant} - ${win.year}`}</td>
                                    <td className="table-cell price-cell">{formatCurrency(win.basePrice)}</td>
                                    <td className="table-cell price-cell">{formatCurrency(win.winningPrice)}</td>
                                    <td className="table-cell">{win.eventName}</td>
                                    <td className="table-cell">{formatDate(win.eventDate)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="no-results">No win history matches your search criteria</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <style jsx="true">{`
                /* General Styles */
                :root {
                    --primary-color: #3498db;
                    --primary-dark: #2980b9;
                    --secondary-color: #2ecc71;
                    --danger-color: #e74c3c;
                    --warning-color: #f39c12;
                    --text-primary: #2c3e50;
                    --text-secondary: #7f8c8d;
                    --bg-primary: #ffffff;
                    --bg-secondary: #f8f9fa;
                    --border-color: #e1e4e8;
                    --shadow-color: rgba(0, 0, 0, 0.1);
                    --border-radius: 8px;
                    --transition-speed: 0.3s;
                }

                .win-history-container {
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    color: var(--text-primary);
                    background-color: white;
                    box-sizing: border-box;
                    border: 2px solid #ee373b;
                    margin: 10px;
                    border-radius: 20px;
                }

                .page-header {
                    margin-bottom: 12px;
                    background-color: #ee373b;
                    text-align: center;
                    border-top-left-radius: 18px;
                    border-top-right-radius: 18px;
                    padding: 10px;
                }

                .page-header h1 {
                    font-size: 24px;
                    font-weight: 600;
                    margin: 0;
                    color: white;
                }

                /* Filters and Search Styles */
                .filters-container {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                    margin-bottom: 12px;
                    padding: 0px 10px;
                    align-items: center;
                }

                .search-container {
                    position: relative;
                    margin-top: 3vh;
                    flex: 1;
                    min-width: 250px;
                }

                .search-icon {
                    position: absolute;
                    top: 50%;
                    left: 12px;
                    transform: translateY(-50%);
                    color: var(--text-secondary);
                    font-style: normal;
                }

                #search-input {
                    width: 100%;
                    padding: 8px 10px 8px 10px;
                    border: 1px solid #ee373b;
                    border-radius: var(--border-radius);
                    font-size: 14px;
                }

                #search-input:focus {
                    outline: none;
                }

                .price-filter {
                    flex: 1;
                    min-width: 250px;
                    display: flex;
                    padding: 0px 25px;
                    flex-direction: column;
                }

                .price-filter label {
                    font-size: 1rem;
                    font-weight: bold;
                    text-align: center;
                    margin-bottom: 4px;
                    color: #ee373b;
                }

                /* Table Styles */
                .table-container {
                    background-color: var(--bg-primary);
                    border-radius: var(--border-radius);
                    overflow-y: scroll;
                    max-height: 55vh;
                    margin-bottom: 1rem;
                    padding: 0px 10px;
                    scrollbar-color: #ee373b #ffffff;
                }

                #win-history-table {
                    width: 100%;
                    border-collapse: collapse;
                    border: none;
                    font-size: 12px;
                }

                #win-history-table th {
                    background-color: var(--bg-secondary);
                    color: var(--text-primary);
                    font-weight: 600;
                    text-align: left;
                    padding: 3px 1px;
                    font-size: 12px;
                    position: sticky;
                    top: 0;
                    border-bottom: 2px solid var(--border-color);
                    cursor: pointer;
                    white-space: nowrap;
                    transition: background-color var(--transition-speed);
                }

                #win-history-table th:hover {
                    background-color: #e9ecef;
                }

                .sort-indicator {
                    color: var(--primary-color);
                    font-weight: bold;
                }

                #win-history-table td {
                    padding: 10px 8px;
                    border-bottom: 1px solid #ee373b;
                    vertical-align: middle;
                    max-width: 100px;
                    font-size: 12px;
                    overflow: hidden;
                    text-wrap: wrap;
                }

                .no-results {
                    text-align: center;
                    padding: 40px 0 !important;
                    color: var(--text-secondary);
                    font-style: italic;
                }

                /* Table Cell Styling */
                .table-cell {
                    max-width: 100px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .price-cell {
                    font-weight: 600;
                    color: var(--text-primary);
                }

                .action-cell {
                    text-align: center;
                }

                .edit-button-enabled {
                    background-color: var(--primary-color);
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 6px 12px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: background-color var(--transition-speed);
                }

                .edit-button-enabled:hover {
                    background-color: var(--primary-dark);
                }

                /* Pagination Styles */
                #pagination-controls {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-top: 24px;
                    gap: 8px;
                }

                .pagination-button {
                    background-color: var(--bg-primary);
                    color: var(--text-primary);
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius);
                    padding: 8px 12px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all var(--transition-speed);
                }

                .pagination-button.active {
                    background-color: var(--primary-color);
                    color: white;
                    border-color: var(--primary-color);
                }

                .pagination-button:hover:not(:disabled) {
                    background-color: var(--primary-color);
                    color: white;
                    border-color: var(--primary-color);
                }

                .pagination-button:disabled {
                    background-color: #f1f3f5;
                    color: #adb5bd;
                    cursor: not-allowed;
                }

                /* Modal Styles */
                #modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.6);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                    animation: fadeIn 0.2s ease-out;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                #modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 550px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    animation: slideIn 0.3s ease-out;
                    overflow: hidden;
                }

                @keyframes slideIn {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between !important;
                    align-items: center;
                    padding: 20px 24px !important;
                    border-bottom: 1px solid var(--border-color);
                }

                #modal-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: white;
                }

                .close-button {
                    background: none;
                    border: none;
                    font-size: 24px;
                    font-weight: bold;
                    color: white;
                    cursor: pointer;
                    transition: color var(--transition-speed);
                }

                .close-button:hover {
                    color: var(--danger-color);
                }

                .vehicle-summary {
                    // background-color: var(--bg-secondary);
                    padding: 20px 24px;
                    align-items: center;
                    border-bottom: 1px solid var(--border-color);
                    display: flex;
                    gap: 1rem;
                }

                .vehicle-summary h3 {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--text-primary);
                }

                .reg-number {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--text-primary);
                }

                #edit-win-form {
                    padding: 24px;
                }

                .form-row {
                    margin-bottom: 20px;
                }

                .form-row label {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-primary);
                }

                .input-with-icon {
                    position: relative;
                }

                .currency-symbol {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--text-secondary);
                    font-weight: 500;
                }

                #edit-win-form input[type="text"] {
                    width: 100%;
                    padding: 10px 10px 10px 32px;
                    border: 1px solid var(--border-color);
                    border-radius: var(--border-radius);
                    font-size: 14px;
                    transition: all var(--transition-speed);
                }

                #edit-win-form input[type="text"]:focus {
                    outline: none;
                    border-color: var(--primary-color);
                    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
                }

                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding: 20px 24px;
                    border-top: 1px solid var(--border-color);
                }

                #modal-cancel-button {
                    padding: 10px 16px;
                    border-radius: var(--border-radius);
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all var(--transition-speed);
                    background-color: white;
                    color: var(--text-primary);
                    border: 1px solid var(--border-color);
                }

                #modal-cancel-button:hover {
                    background-color: var(--bg-secondary);
                    border-color: var(--text-secondary);
                }

                /* Responsive Styles */
                @media (max-width: 1200px) {
                    .table-container {
                        overflow-x: auto;
                    }

                    #win-history-table {
                        min-width: 1000px;
                    }
                }

                @media (max-width: 768px) {
                    .filters-container {
                        flex-direction: column;
                        align-items: stretch;
                    }

                    .pagination-info {
                        display: none;
                    }

                    #pagination-controls {
                        justify-content: space-between;
                    }

                    .date-range-container {
                        flex-direction: column;
                        gap: 10px;
                    }

                    #modal-content {
                        width: 95%;
                    }
                }

                @media (max-width: 480px) {
                    .win-history-container {
                        padding: 10px;
                    }

                    .page-header h1 {
                        font-size: 20px;
                    }

                    .edit-button-enabled {
                        padding: 6px 10px;
                        font-size: 12px;
                    }

                    #modal-content {
                        width: 100%;
                        border-radius: 0;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                    }

                    .modal-header {
                        padding: 16px;
                    }

                    .vehicle-summary {
                        padding: 16px;
                    }

                    #edit-win-form {
                        padding: 16px;
                        flex: 1;
                        overflow-y: auto;
                    }

                    .modal-footer {
                        padding: 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default WinHistory;
